/* The languages the Deadlock pages are written in, beyond English.

   Each language gets the two pages people search for in it: the main
   "Deadlock cheats" page at /<code> and the free-version page at
   /<code>/free-deadlock-cheat. Both are full pages written for that language's
   searches, not machine output, and each links to its English counterpart
   through hreflang so Google shows the right one per country.

   Nobody is redirected. A crawler requesting the English page from anywhere
   must get the English page, or Google indexes a mixture; visitors whose
   browser prefers one of these languages are offered the page in a banner
   instead (LanguageSuggest). */

export const LANGS = ["ru", "de", "pt", "zh", "es"] as const;
export type Lang = (typeof LANGS)[number];

export type LangInfo = {
  /** <html lang> and hreflang. */
  hreflang: string;
  /** In its own language, for the switcher. */
  native: string;
  /** Browser languages (primary subtags) this page is offered to. */
  browser: string[];
  /** Time zones that suggest this language when the browser says English.
      Mostly for the Russian-speaking countries, where an English-language OS
      is common. */
  zones: string[];
  /** The banner, in the language it offers. */
  offer: string;
  open: string;
  ui: {
    home: string;
    products: string;
    updated: string;
    onThisPage: string;
    faq: string;
    faqTitle: string;
    watch: string;
    buyFrom: (entry: string) => string;
    see: string;
    compare: string;
    readyTitle: string;
    readyBody: string;
    askDiscord: string;
    plans: string;
    paused: string;
    from: string;
    more: string;
    english: string;
    dateLocale: string;
    /** The plan units the price feed uses ("week", "month"...), translated. */
    units: Record<string, string>;
  };
};

export const LANG_INFO: Record<Lang, LangInfo> = {
  ru: {
    hreflang: "ru",
    native: "Русский",
    /* Russian, and the languages of countries where Russian is widely
       understood: Ukrainian, Belarusian, Kazakh, Kyrgyz, Uzbek, Tajik,
       Turkmen, Armenian, Azerbaijani, Georgian, Moldovan/Romanian is left
       out on purpose (most Romanian speakers are in Romania). */
    browser: ["ru", "uk", "be", "kk", "ky", "uz", "tg", "tk", "hy", "az", "ka"],
    zones: [
      "Europe/Moscow", "Europe/Kaliningrad", "Europe/Samara", "Europe/Volgograd", "Europe/Kirov", "Europe/Ulyanovsk",
      "Europe/Astrakhan", "Europe/Saratov", "Asia/Yekaterinburg", "Asia/Omsk", "Asia/Novosibirsk", "Asia/Barnaul",
      "Asia/Tomsk", "Asia/Novokuznetsk", "Asia/Krasnoyarsk", "Asia/Irkutsk", "Asia/Chita", "Asia/Yakutsk",
      "Asia/Khandyga", "Asia/Vladivostok", "Asia/Ust-Nera", "Asia/Magadan", "Asia/Sakhalin", "Asia/Srednekolymsk",
      "Asia/Kamchatka", "Asia/Anadyr", "Europe/Minsk", "Europe/Kiev", "Europe/Kyiv", "Europe/Simferopol",
      "Asia/Almaty", "Asia/Qostanay", "Asia/Aqtobe", "Asia/Aqtau", "Asia/Atyrau", "Asia/Oral", "Asia/Qyzylorda",
      "Asia/Bishkek", "Asia/Tashkent", "Asia/Samarkand", "Asia/Dushanbe", "Asia/Ashgabat", "Asia/Yerevan",
      "Asia/Baku", "Asia/Tbilisi", "Europe/Chisinau",
    ],
    offer: "Есть версия сайта на русском",
    open: "Открыть",
    ui: {
      home: "Главная",
      products: "Продукты",
      updated: "Обновлено",
      onThisPage: "На этой странице",
      faq: "Вопросы",
      faqTitle: "Частые вопросы",
      watch: "Как это выглядит в игре",
      buyFrom: (e) => `Купить чит на Deadlock — от ${e}`,
      see: "Смотреть чит на Deadlock",
      compare: "Internal или External",
      readyTitle: "Готовы начать?",
      readyBody: "Одна оплата, доступ сразу после неё, без автопродления. Сначала есть вопросы?",
      askDiscord: "Спросите в Discord",
      plans: "Тарифы и цены →",
      paused: "Продажи приостановлены",
      from: "от",
      more: "Ещё на русском",
      english: "English version",
      dateLocale: "ru-RU",
      units: { week: "неделю", month: "месяц", quarter: "квартал", "6 months": "6 месяцев", lifetime: "навсегда" },
    },
  },
  de: {
    hreflang: "de",
    native: "Deutsch",
    browser: ["de"],
    zones: [],
    offer: "Diese Seite gibt es auch auf Deutsch",
    open: "Öffnen",
    ui: {
      home: "Start",
      products: "Produkte",
      updated: "Aktualisiert",
      onThisPage: "Auf dieser Seite",
      faq: "Fragen",
      faqTitle: "Häufige Fragen",
      watch: "So sieht es im Spiel aus",
      buyFrom: (e) => `Deadlock Cheat holen — ab ${e}`,
      see: "Zum Deadlock Cheat",
      compare: "Internal oder External",
      readyTitle: "Bereit?",
      readyBody: "Eine Zahlung, Zugang sobald sie durch ist, keine automatische Verlängerung. Erst Fragen?",
      askDiscord: "Frag im Discord",
      plans: "Pläne & Preise →",
      paused: "Verkauf pausiert",
      from: "ab",
      more: "Mehr auf Deutsch",
      english: "English version",
      dateLocale: "de-DE",
      units: { week: "Woche", month: "Monat", quarter: "Quartal", "6 months": "6 Monate", lifetime: "für immer" },
    },
  },
  pt: {
    hreflang: "pt-BR",
    native: "Português (Brasil)",
    browser: ["pt"],
    zones: [],
    offer: "Este site também está em português",
    open: "Abrir",
    ui: {
      home: "Início",
      products: "Produtos",
      updated: "Atualizado",
      onThisPage: "Nesta página",
      faq: "Perguntas",
      faqTitle: "Perguntas frequentes",
      watch: "Veja no jogo",
      buyFrom: (e) => `Pegar o hack de Deadlock — a partir de ${e}`,
      see: "Ver o hack de Deadlock",
      compare: "Internal ou External",
      readyTitle: "Bora jogar?",
      readyBody: "Um pagamento, acesso assim que ele cair, sem renovação automática. Tem dúvida antes?",
      askDiscord: "Pergunte no Discord",
      plans: "Planos e preços →",
      paused: "Vendas pausadas",
      from: "a partir de",
      more: "Mais em português",
      english: "English version",
      dateLocale: "pt-BR",
      units: { week: "semana", month: "mês", quarter: "trimestre", "6 months": "6 meses", lifetime: "vitalício" },
    },
  },
  zh: {
    hreflang: "zh-Hans",
    native: "简体中文",
    browser: ["zh"],
    zones: [],
    offer: "本站有简体中文版",
    open: "打开",
    ui: {
      home: "首页",
      products: "产品",
      updated: "更新于",
      onThisPage: "本页内容",
      faq: "常见问题",
      faqTitle: "常见问题",
      watch: "游戏内实拍",
      buyFrom: (e) => `购买 Deadlock 辅助 — ${e} 起`,
      see: "查看 Deadlock 辅助",
      compare: "内部版与外部版",
      readyTitle: "准备好了吗？",
      readyBody: "一次付款，付款完成即可使用，不会自动续费。有问题想先问？",
      askDiscord: "在 Discord 提问",
      plans: "套餐与价格 →",
      paused: "暂停销售",
      from: "起",
      more: "更多中文页面",
      english: "English version",
      dateLocale: "zh-CN",
      units: { week: "周", month: "月", quarter: "季", "6 months": "半年", lifetime: "永久" },
    },
  },
  es: {
    hreflang: "es",
    native: "Español",
    browser: ["es"],
    zones: [],
    offer: "Esta web también está en español",
    open: "Abrir",
    ui: {
      home: "Inicio",
      products: "Productos",
      updated: "Actualizado",
      onThisPage: "En esta página",
      faq: "Preguntas",
      faqTitle: "Preguntas frecuentes",
      watch: "Míralo en el juego",
      buyFrom: (e) => `Consigue el hack de Deadlock — desde ${e}`,
      see: "Ver el hack de Deadlock",
      compare: "Internal o External",
      readyTitle: "¿Listo?",
      readyBody: "Un solo pago, acceso en cuanto se confirma, sin renovación automática. ¿Dudas antes?",
      askDiscord: "Pregunta en Discord",
      plans: "Planes y precios →",
      paused: "Ventas en pausa",
      from: "desde",
      more: "Más en español",
      english: "English version",
      dateLocale: "es-ES",
      units: { week: "semana", month: "mes", quarter: "trimestre", "6 months": "6 meses", lifetime: "de por vida" },
    },
  },
};

/** The language a visitor would probably rather read, or null for English. */
export function preferredLang(): Lang | null {
  if (typeof navigator === "undefined") return null;
  const tags = (navigator.languages?.length ? navigator.languages : [navigator.language]).filter(Boolean);
  for (const tag of tags) {
    const primary = tag.toLowerCase().split("-")[0];
    if (primary === "en") break; // English listed first: they chose it
    const hit = LANGS.find((l) => LANG_INFO[l].browser.includes(primary));
    if (hit) return hit;
  }
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const hit = LANGS.find((l) => LANG_INFO[l].zones.includes(zone));
    if (hit) return hit;
  } catch {
    /* no Intl: English */
  }
  return null;
}

/** The language a path is in, from its first segment. */
export function langOfPath(pathname: string): Lang | null {
  const first = pathname.replace(/^\/+/, "").split("/")[0];
  return (LANGS as readonly string[]).includes(first) ? (first as Lang) : null;
}
